import { ApiRequestError } from "../api/client.js";
import type { FieldError } from "../api/types.js";

const FIELD_LABELS: Record<string, string> = {
  lastname: "Priimek",
  name: "Ime",
  carMaker: "Znamka avta",
  registrationNumber: "Reg. številka",
  phone: "Telefon",
  notes: "Opombe",
  tireSets: "Garniture gum",
  employeeCode: "Koda",
};

function describeFieldError(error: FieldError): string {
  const [rootField] = error.path.split(".");
  const label = (rootField && FIELD_LABELS[rootField]) ?? error.path;
  return `${label} (${error.code})`;
}

/**
 * Turns a thrown `ApiRequestError` — or anything else — into a Slovenian
 * message fit to show at the counter. Several exact strings are the legacy
 * `MessageBox.Show(...)` wording, kept verbatim where the underlying
 * condition is identical (e.g. "Nepravilna koda." — `getUserFromCode`,
 * AddItemWindow.xaml.cs:310).
 */
export function describeApiError(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return error instanceof Error ? error.message : String(error);
  }

  const failure = error.failure;
  switch (failure.kind) {
    case "validation":
      return `Obrazec ni pravilno izpolnjen: ${failure.errors.map(describeFieldError).join(", ")}`;
    case "unauthorized":
      return describeUnauthorized(failure.reason);
    case "forbidden":
      return "Nimate pravic za to dejanje.";
    case "rate-limited":
      return "Preveč neuspelih poskusov. Poskusite kasneje.";
    case "not-found":
      return "Vnosa ni bilo mogoče najti.";
    case "conflict":
      return describeConflict(failure.reason, failure.conflictingNumber);
    case "network":
      return "Napaka povezave s strežnikom.";
    case "unknown":
      return failure.message;
  }
}

function describeUnauthorized(reason: "invalid-code" | "inactive-employee" | "invalid-credentials" | undefined): string {
  switch (reason) {
    case "invalid-code":
      return "Nepravilna koda.";
    case "inactive-employee":
      return "Koda pripada neaktivnemu zaposlenemu.";
    case "invalid-credentials":
      // Same wording regardless of whether the email exists — no user enumeration.
      return "Napačen e-poštni naslov ali geslo.";
    case undefined:
      return "Seja je potekla. Prijavite se znova.";
  }
}

function describeConflict(reason: string, conflictingNumber?: number): string {
  switch (reason) {
    case "swap-item-not-found":
      return "Izbranega vnosa za dvig ni bilo mogoče najti.";
    case "customer-mismatch":
      return "Izbrani vnos za dvig ne pripada tej stranki.";
    case "already-picked-up":
      return "Vnos je že označen kot dvignjen.";
    case "not-picked-up":
      return "Vnos še ni bil dvignjen.";
    case "deleted":
      return "Vnos je izbrisan.";
    case "already-deleted":
      return "Vnos je že izbrisan.";
    case "not-deleted":
      return "Vnos ni izbrisan.";
    case "number-taken":
      return `Mesto št. ${conflictingNumber ?? "?"} je zdaj zasedeno — preverite fizično stanje na polici.`;
    case "duplicate-target-label":
      return "Sezona s tem imenom že obstaja.";
    default:
      return `Napaka: ${reason}`;
  }
}
